import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MenuItem, Stack, Typography } from '@mui/material';
import Input from '../ui/forms/fields/input';
import PrimaryButton from '../ui/buttons/primaryButton';
import { signInUserWithToken } from '../../services/auth';
import { useAlert } from '../../contexts/alertContext';
import { useNavigate, useParams } from 'react-router-dom';
import AuthTemplate from './authTemplate';
import { getVenues } from '../../features/venue/venueSlice';
import Select from '../ui/forms/fields/select';
import { getUserByInviteToken, verifyInviteToken } from '../../features/user/userSlice';
import PhoneNumberInput from '../ui/forms/fields/phoneNumberInput';
import { useAuth } from '../../contexts/authContext';

const schema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().nullable(),
  password: yup.string().required('Password is required'),
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required')
});

const Invite = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSigningUp, setIsSigningUp] = React.useState(false);
  const [isAcceptingExisting, setIsAcceptingExisting] = React.useState(false);
  const { showAlertSuccess, showAlertError } = useAlert();
  const { authenticated, currentUser } = useAuth();
  const venues = useSelector((state) => state.venue.venues);
  const loadingVenues = useSelector((state) => state.loadingVenues);
  const loadingUser = useSelector((state) => state.user.loading);

  const userDetails = useSelector((state) => state.user.invitedUser);
  const userInviteToken = useParams().inviteToken;

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      clubId: ''
    }
  });

  const didHydrateInviteUserRef = useRef(false);
  const emailValue = watch('email');
  const isExistingUserInvite = Boolean(userDetails?.existingUser);

  const normalizeOptionalField = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) {
      return '';
    }

    if (normalized.toLowerCase() === 'undefined' || normalized.toLowerCase() === 'null') {
      return '';
    }

    return normalized;
  };

  const sortedVenues = useMemo(() => {
    return [...venues].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [venues]);

  useEffect(() => {
    dispatch(getVenues());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getUserByInviteToken(userInviteToken));
  }, [dispatch, userInviteToken]);

  useEffect(() => {
    if (userDetails && !didHydrateInviteUserRef.current) {
      setValue('email', normalizeOptionalField(userDetails.email));
      setValue('phone', normalizeOptionalField(userDetails.phone));
      setValue('firstName', normalizeOptionalField(userDetails.firstName));
      setValue('lastName', normalizeOptionalField(userDetails.lastName));
      didHydrateInviteUserRef.current = true;
    }
  }, [userDetails, setValue]);

  const onSignUp = async (data) => {
    if (!isSigningUp) {
      setIsSigningUp(true);
      try {
        const response = await dispatch(
          verifyInviteToken({
            ...data,
            token: userInviteToken
          })
        ).unwrap();

        if (response?.customAuthToken) {
          await signInUserWithToken(response.customAuthToken);
          showAlertSuccess('Successfully signed in!');
        } else {
          showAlertSuccess('Invite accepted successfully.');
        }

        navigate('/overview');
      } catch (err) {
        showAlertError(err || err?.message || 'Please check user information and try again.');
      } finally {
        setIsSigningUp(false);
      }
    }
  };

  const handleContinueToLogin = () => {
    navigate(`/signin?redirect=${encodeURIComponent(`/invite/${userInviteToken}`)}`);
  };

  const handleAcceptExistingInvite = async () => {
    if (!authenticated || !currentUser?.uid) {
      showAlertError('Please sign in before accepting this invite.');
      return;
    }

    if (!isAcceptingExisting) {
      setIsAcceptingExisting(true);
      try {
        await dispatch(
          verifyInviteToken({
            token: userInviteToken,
            currentUserId: currentUser.uid
          })
        ).unwrap();

        showAlertSuccess('Invite accepted successfully.');
        navigate('/overview');
      } catch (error) {
        showAlertError(error || error?.message || 'Unable to accept invite.');
      } finally {
        setIsAcceptingExisting(false);
      }
    }
  };

  if (loadingUser) {
    return (
      <AuthTemplate title="Loading invite" subtitle="We are checking your invitation details.">
        <Typography variant="body1">Loading invite...</Typography>
      </AuthTemplate>
    );
  }

  if (!userDetails) {
    return (
      <AuthTemplate title="Invite unavailable" subtitle="This invite is invalid or has expired.">
        <Typography variant="body1">This invite is invalid or has expired.</Typography>
      </AuthTemplate>
    );
  }

  if (isExistingUserInvite && !authenticated) {
    return (
      <AuthTemplate
        title="Workspace invite"
        subtitle={`You were invited to join ${userDetails.workspaceName}.`}>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
          This email is already registered. Continue to login, then accept the invite.
        </Typography>
        <PrimaryButton fullWidth variant="contained" color="primary" size="large" onClick={handleContinueToLogin}>
          Continue to Login
        </PrimaryButton>
      </AuthTemplate>
    );
  }

  if (isExistingUserInvite && authenticated) {
    return (
      <AuthTemplate
        title="Accept invite"
        subtitle={`You were invited to join ${userDetails.workspaceName}.`}>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
          Accept this invite to continue to your workspace.
        </Typography>
        <PrimaryButton
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          onClick={handleAcceptExistingInvite}
          disabled={isAcceptingExisting}>
          {isAcceptingExisting ? 'Accepting...' : 'Accept Invite'}
        </PrimaryButton>
      </AuthTemplate>
    );
  }

  return (
    <AuthTemplate
      title="Create your invited account"
      subtitle={`Complete your details to join ${userDetails.workspaceName || 'this workspace'}.`}>
      <Stack spacing={1.5}>
        <Input
          label="First Name"
          variant="outlined"
          autoComplete="given-name"
          {...register('firstName')}
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
        />
        <Input
          label="Last Name"
          variant="outlined"
          autoComplete="family-name"
          {...register('lastName')}
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
        />
        <Input
          label="Email"
          variant="outlined"
          autoComplete="email"
          InputLabelProps={{ shrink: Boolean(emailValue) }}
          disabled
          {...register('email')}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
        <PhoneNumberInput
          control={control}
          name="phone"
          label="Phone"
          defaultValue=""
          error={errors.phone}
          helperText={errors.phone?.message}
        />

        <Input
          label="Password"
          variant="outlined"
          type="password"
          autoComplete="new-password"
          {...register('password')}
          error={!!errors.password}
          helperText={errors.password?.message}
        />

        {loadingVenues ? (
          <Typography>Loading clubs...</Typography>
        ) : (
          <Select control={control} name="clubId" label="Select a Club">
            {sortedVenues.map((venue) => (
              <MenuItem key={venue.id} value={venue.id}>
                {venue.name}
              </MenuItem>
            ))}
          </Select>
        )}

        <PrimaryButton
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          disabled={isSigningUp}
          onClick={() => handleSubmit(onSignUp)()}>
          {isSigningUp ? 'Joining...' : 'Continue'}
        </PrimaryButton>
      </Stack>
    </AuthTemplate>
  );
};

export default Invite;
